<script lang="ts">
	import Calendar from 'lucide-svelte/icons/calendar'
	import { onMount } from 'svelte'
	import { writable } from 'svelte/store'
	import { queryParam } from 'sveltekit-search-params'

	import { afterNavigate, beforeNavigate } from '$app/navigation'
	import { Button } from '$lib/components/ui/button'
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu'
	import type { MetricsRange } from '$lib/models'
	import * as m from '$lib/paraglide/messages'
	import { navigationStore } from '$lib/stores/navigation'
	import { phg } from '$lib/utils/posthog'
	import { cn } from '$lib/utils/shadcn'

	let className: string | undefined | null = undefined
	export { className as class }
	export let isLoading: boolean
	export let descriptionType: 'long' | 'short' = 'long'
	export let place:
		| 'table_controls'
		| 'chain_page_controls'
		| 'compare_page_controls'
		| 'chart_page_controls'

	const metricsRangeQuery = queryParam<MetricsRange>('range-cm')
	const metricsRange = writable<MetricsRange>(
		$metricsRangeQuery === 'hour' ||
			$metricsRangeQuery === 'day' ||
			$metricsRangeQuery === 'week' ||
			$metricsRangeQuery === 'month'
			? $metricsRangeQuery
			: 'hour'
	)

	let loaderTimeout: ReturnType<typeof setTimeout>

	beforeNavigate(({ from, to }) => {
		if (!from || !to) return

		const navigatingWithinPage = from.url.pathname === to.url.pathname
		const queryParamChanged =
			from.url.searchParams.get('range-cm') !== to.url.searchParams.get('range-cm')

		if (navigatingWithinPage && queryParamChanged) {
			clearTimeout(loaderTimeout)

			loaderTimeout = setTimeout(() => {
				isLoading = true
				navigationStore.set('loading')
			}, 100)
		}
	})

	afterNavigate(() => {
		clearTimeout(loaderTimeout)

		isLoading = false
		navigationStore.set('loaded')
	})

	onMount(() => {
		const unsubscibeFromChartRangeQuery = metricsRangeQuery.subscribe(value => {
			if (value === 'hour' || value === 'day' || value === 'week' || value === 'month') {
				metricsRange.set(value)
			} else {
				metricsRange.set('hour')
			}
		})

		const unsubscribeFromMetricsRange = metricsRange.subscribe(value => {
			if (value !== $metricsRangeQuery) {
				if (value === 'hour') {
					metricsRangeQuery.set(null)
				} else {
					metricsRangeQuery.set(value)
				}
			}
		})

		return () => {
			unsubscibeFromChartRangeQuery()
			unsubscribeFromMetricsRange()
		}
	})

	const metricRanges: {
		label: string
		value: MetricsRange
	}[] = [
		{
			label: '1 hour',
			value: 'hour',
		},
		{
			label: '1 day',
			value: 'day',
		},
		{
			label: '7 days',
			value: 'week',
		},
		{
			label: '30 days',
			value: 'month',
		},
	]
</script>

<DropdownMenu.Root preventScroll={false}>
	<DropdownMenu.Trigger asChild let:builder>
		<Button
			class={className}
			aria-label={m.metric_ranges_filter_label()}
			builders={[builder]}
			disabled={isLoading}
			size="responsiveIcon"
			variant="outline"
		>
			<Calendar size="16" />

			<span class="hidden sm:ml-2 sm:inline">
				{metricRanges.find(({ value }) => value === $metricsRange)?.label ||
					m.metric_ranges_filter_label()}
			</span>
		</Button>
	</DropdownMenu.Trigger>

	<DropdownMenu.Content class={descriptionType === 'long' ? 'w-52' : 'w-36'}>
		<DropdownMenu.Label
			class={cn('text-2xs text-muted-foreground block', {
				'text-center': descriptionType === 'short',
			})}
		>
			{#if descriptionType === 'long'}
				{m.metric_ranges_filter_description_long()}
			{:else}
				{m.metric_ranges_filter_description_short()}
			{/if}
		</DropdownMenu.Label>

		<DropdownMenu.Separator />

		<DropdownMenu.RadioGroup
			onValueChange={newValue => {
				phg.capture('metrics_range_click', {
					place,
					value: newValue,
				})
			}}
			bind:value={$metricsRange}
		>
			{#each metricRanges as { value, label } (value)}
				<DropdownMenu.RadioItem {value}>
					{label}
				</DropdownMenu.RadioItem>
			{/each}
		</DropdownMenu.RadioGroup>
	</DropdownMenu.Content>
</DropdownMenu.Root>
